import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type {
  Cac,
  CampoOrdenacaoNegocio,
  Leads,
  ListaDeNegocios,
  MetasAnuais,
  MetasDoMes,
  Preset,
  Relatorio,
  SerieHistorica,
} from '../types'

import { api } from '@/lib/api-client'

export interface FiltroPeriodo {
  preset?: Preset
  inicio?: string
  fim?: string
}

function paramsDoFiltro(filtro: FiltroPeriodo): Record<string, string> {
  if (filtro.inicio && filtro.fim) return { inicio: filtro.inicio, fim: filtro.fim }
  return { preset: filtro.preset ?? 'mes' }
}

// Cache do cliente: revisitar um período/aba já vistos não refaz a busca por
// 5 min — o servidor já cacheia, isto evita até o round-trip.
const STALE_MS = 5 * 60_000

export function useRelatorio(filtro: FiltroPeriodo) {
  const params = paramsDoFiltro(filtro)

  return useQuery({
    queryKey: ['relatorio', params],
    queryFn: async () => {
      const { data } = await api.get<Relatorio>('/api/relatorio', { params })
      return data
    },
    // Ao trocar o período, mantém o relatório anterior enquanto o novo carrega —
    // sem flash de skeleton no painel e sem fechar o modo apresentação.
    placeholderData: (anterior) => anterior,
    staleTime: STALE_MS,
  })
}

export function useCac(filtro: FiltroPeriodo) {
  const params = paramsDoFiltro(filtro)

  return useQuery({
    queryKey: ['relatorio', 'cac', params],
    queryFn: async () => {
      const { data } = await api.get<Cac>('/api/relatorio/cac', { params })
      return data
    },
    placeholderData: (anterior) => anterior,
    staleTime: STALE_MS,
  })
}

export function useLeads(filtro: FiltroPeriodo) {
  const params = paramsDoFiltro(filtro)

  return useQuery({
    queryKey: ['relatorio', 'leads', params],
    queryFn: async () => {
      const { data } = await api.get<Leads>('/api/relatorio/leads', { params })
      return data
    },
    placeholderData: (anterior) => anterior,
    staleTime: STALE_MS,
  })
}

export function useSerieHistorica(meses = 12) {
  return useQuery({
    queryKey: ['relatorio', 'serie', meses],
    queryFn: async () => {
      const { data } = await api.get<SerieHistorica>('/api/relatorio/serie', { params: { meses } })
      return data
    },
    staleTime: STALE_MS,
  })
}

export function useMetasAnuais(ano: number) {
  return useQuery({
    queryKey: ['metas', 'anuais', ano],
    queryFn: async () => {
      const { data } = await api.get<MetasAnuais>('/api/metas/anuais', { params: { ano } })
      return data
    },
  })
}

export interface SalvarMetasAnuaisInput {
  ano: number
  niveis: { nivel: number; valor: number }[]
}

export function useSalvarMetasAnuais() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async (input: SalvarMetasAnuaisInput) => {
      const { data } = await api.put('/api/metas/anuais', input)
      return data
    },
    onSuccess: async (_data, input) => {
      // A meta anual muda o RAG da projeção na série do painel.
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['metas', 'anuais', input.ano] }),
        qc.invalidateQueries({ queryKey: ['relatorio'] }),
      ])
    },
  })
}

export interface FiltroNegocios extends FiltroPeriodo {
  page: number
  perPage: number
  sortBy: CampoOrdenacaoNegocio
  sortDir: 'asc' | 'desc'
  search?: string
  vendedor?: string
  produto?: string
  canal?: string
  meioPagamento?: string
  valorMin?: number
  valorMax?: number
  comDesconto?: boolean
  comPendencia?: boolean
}

/** Só o que está preenchido vira query — filtro vazio não deve virar `?canal=`. */
function paramsDosNegocios({
  preset,
  inicio,
  fim,
  ...filtro
}: FiltroNegocios): Record<string, string> {
  const params: Record<string, string> = paramsDoFiltro({ preset, inicio, fim })

  for (const [chave, valor] of Object.entries(filtro)) {
    if (valor === undefined || valor === '' || valor === false) continue
    params[chave] = String(valor)
  }

  return params
}

export function useNegocios(filtro: FiltroNegocios) {
  const params = paramsDosNegocios(filtro)

  return useQuery({
    queryKey: ['relatorio', 'negocios', params],
    queryFn: async () => {
      const { data } = await api.get<ListaDeNegocios>('/api/relatorio/negocios', { params })
      return data
    },
    placeholderData: (anterior) => anterior,
    staleTime: STALE_MS,
  })
}

export const FORMATOS_EXPORT = ['csv', 'xlsx', 'pdf'] as const
export type FormatoExport = (typeof FORMATOS_EXPORT)[number]

/** Nome sugerido pelo servidor no `Content-Disposition`, com fallback. */
function nomeDoAnexo(disposition: string | undefined, formato: FormatoExport): string {
  const encontrado = /filename="([^"]+)"/.exec(disposition ?? '')
  return encontrado?.[1] ?? `relatorio-comercial.${formato}`
}

/**
 * Download por blob, não por `window.open`: a rota exige cookie de sessão e
 * precisa dos mesmos filtros da tela na query.
 */
export async function baixarExport(
  formato: FormatoExport,
  filtro: Omit<FiltroNegocios, 'page' | 'perPage'>,
): Promise<void> {
  const params = paramsDosNegocios({ ...filtro, page: 1, perPage: 1 })
  delete params.page
  delete params.perPage

  const resposta = await api.get<Blob>('/api/relatorio/export', {
    params: { ...params, formato },
    responseType: 'blob',
  })

  const url = URL.createObjectURL(resposta.data)
  const link = document.createElement('a')
  link.href = url
  link.download = nomeDoAnexo(
    resposta.headers['content-disposition'] as string | undefined,
    formato,
  )
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

export function useMetas(mes: string) {
  return useQuery({
    queryKey: ['metas', mes],
    queryFn: async () => {
      const { data } = await api.get<MetasDoMes>('/api/metas', { params: { mes } })
      return data
    },
  })
}

export interface SalvarMetasInput {
  mes: string
  empresa: number
  porVendedor: { vendedorId: string; valor: number }[]
}

export function useSalvarMetas() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async (input: SalvarMetasInput) => {
      const { data } = await api.put('/api/metas', input)
      return data
    },
    onSuccess: async (_data, input) => {
      // A meta muda o atingimento e o RAG de todo o relatório.
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['metas', input.mes] }),
        qc.invalidateQueries({ queryKey: ['relatorio'] }),
      ])
    },
  })
}
